import { Select } from '@ardoq/select';
import { TextInput } from '@ardoq/forms';
import {
  FilterInfoOperator,
  FormattingOperator,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import { isEmpty, sum } from 'lodash';
import { QueryBuilderFilterType } from '@ardoq/query-builder';
import { type I18nLocale, t } from '@ardoq/i18n';
import { useFormattingContext } from '../useFormattingContext';

export const DEFAULT_OPERATOR_LABEL_KEY = Symbol('DEFAULT_OPERATOR_LABEL_KEY');

/**
 * An operator label is either a plain string (used as-is) or a
 * `{key, default}` translatable record. The per-filterType map shape is also
 * supported, with each per-type value being a string or translatable record.
 */
type LabelLeaf = string | TranslatableLabel;
type TranslatableLabel = { key: string; default: string };

export type OperatorLabel =
  | LabelLeaf
  | (Partial<Record<QueryBuilderFilterType, LabelLeaf>> & {
      [DEFAULT_OPERATOR_LABEL_KEY]: LabelLeaf;
    });

const tr = (key: string, default_: string): TranslatableLabel => ({
  key,
  default: default_,
});

const isTranslatableLabel = (
  label: OperatorLabel
): label is TranslatableLabel =>
  typeof label === 'object' &&
  label !== null &&
  typeof (label as TranslatableLabel).key === 'string' &&
  typeof (label as TranslatableLabel).default === 'string';

const renderLeaf = (i18nLocale: I18nLocale, leaf: LabelLeaf): string =>
  typeof leaf === 'string' ? leaf : t(i18nLocale, leaf.key, leaf.default);

const comparatorToSelectOption =
  (
    i18nLocale: I18nLocale,
    filterType?: QueryBuilderFilterType,
    useNewLabels?: boolean
  ) =>
  (comparator: FilterInfoOperator | FormattingRuleOperator) => ({
    label: resolveOperatorLabel(
      i18nLocale,
      comparator,
      filterType,
      useNewLabels
    ),
    value: comparator,
  });
const isTwoDimensional = <T,>(array: T[] | T[][]): array is T[][] =>
  array.some(Array.isArray);

type SelectOperatorProps = {
  /** to group options into sections, use a two-dimensional array of comparators. */
  comparators: FormattingOperator[] | FormattingOperator[][];
  selectedComparator?: FormattingOperator;
  onValueChange: (comparator?: FormattingOperator) => void;
  clickId?: string;
  isNewConditionalFormatting: boolean;
  menuPortalTarget?: HTMLElement | null;
  filterType?: QueryBuilderFilterType;
};

const filterInfoOperatorLabels: Record<FormattingOperator, OperatorLabel> = {
  [FilterInfoOperator.ID_MATCH]: tr('perspectives:operators.equals', 'equals'),
  [FilterInfoOperator.DATE_MATCH]: tr('perspectives:operators.is', 'is'),
  [FilterInfoOperator.DATE_EARLIER]: tr(
    'perspectives:operators.before',
    'before'
  ),
  [FilterInfoOperator.DATE_LATER]: tr('perspectives:operators.after', 'after'),
  [FilterInfoOperator.BOOLEAN]: tr(
    'perspectives:operators.isTrueFalse',
    'is true / false'
  ),
  [FilterInfoOperator.EQUALS]: tr('perspectives:operators.equals', 'equals'),
  [FilterInfoOperator.LESS_THAN]: tr(
    'perspectives:operators.lessThan',
    'less than'
  ),
  [FilterInfoOperator.GREATER_THAN]: tr(
    'perspectives:operators.greaterThan',
    'greater than'
  ),
  [FilterInfoOperator.CONTAINS]: tr(
    'perspectives:operators.contains',
    'contains'
  ),
  [FilterInfoOperator.DATE_RANGE_AFTER]: tr(
    'perspectives:operators.after',
    'after'
  ),
  [FilterInfoOperator.DATE_RANGE_BEFORE]: tr(
    'perspectives:operators.before',
    'before'
  ),
  [FilterInfoOperator.DATE_RANGE_CONTAINS]: tr(
    'perspectives:operators.contains',
    'contains'
  ),
  [FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN]: tr(
    'perspectives:operators.doesNotContain',
    'does not contain'
  ),
  [FormattingRuleOperator.COLOR_RANGE]: tr(
    'perspectives:operators.colorRange',
    'color range'
  ),
  [FormattingRuleOperator.ID_DOES_NOT_MATCH]: tr(
    'perspectives:operators.notEqualTo',
    'not equal to'
  ),
  [FormattingRuleOperator.NOT_EQUAL]: tr(
    'perspectives:operators.notEqualTo',
    'not equal to'
  ),
  [FormattingRuleOperator.NOT_CONTAINS]: tr(
    'perspectives:operators.doesNotContain',
    'does not contain'
  ),
  [FormattingRuleOperator.NOT_BOOLEAN]: tr(
    'perspectives:operators.unchecked',
    'unchecked'
  ),
  [FormattingRuleOperator.GREATER_THAN_OR_EQUAL_TO]: tr(
    'perspectives:operators.greaterThanOrEqualTo',
    'greater than or equal to'
  ),
  [FormattingRuleOperator.LESS_THAN_OR_EQUAL_TO]: tr(
    'perspectives:operators.lessThanOrEqualTo',
    'less than or equal to'
  ),
  [FormattingRuleOperator.BETWEEN]: tr(
    'perspectives:operators.isBetween',
    'is between'
  ),
  [FormattingRuleOperator.NOT_BETWEEN]: tr(
    'perspectives:operators.isNotBetween',
    'is not between'
  ),
  [FormattingRuleOperator.DATE_BETWEEN]: tr(
    'perspectives:operators.isBetween',
    'is between'
  ),
  [FormattingRuleOperator.DATE_NOT_BETWEEN]: tr(
    'perspectives:operators.isNotBetween',
    'is not between'
  ),
  [FormattingRuleOperator.DATE_NOT_EQUAL]: tr(
    'perspectives:operators.isNot',
    'is not'
  ),
  [FormattingRuleOperator.DATE_ON_OR_BEFORE]: tr(
    'perspectives:operators.isOnOrBefore',
    'is on or before'
  ),
  [FormattingRuleOperator.DATE_ON_OR_AFTER]: tr(
    'perspectives:operators.isOnOrAfter',
    'is on or after'
  ),
  [FormattingRuleOperator.DATE_RANGE_ON_OR_BEFORE]: tr(
    'perspectives:operators.isOnOrBefore',
    'is on or before'
  ),
  [FormattingRuleOperator.DATE_RANGE_ON_OR_AFTER]: tr(
    'perspectives:operators.isOnOrAfter',
    'is on or after'
  ),
  [FormattingRuleOperator.DATE_RANGE_BETWEEN]: tr(
    'perspectives:operators.isBetween',
    'is between'
  ),
  [FormattingRuleOperator.RELATIVE_DATE]: tr(
    'perspectives:operators.relativeDate',
    'Relative date'
  ),
  [FormattingRuleOperator.IS_EMPTY]: tr(
    'perspectives:operators.isEmpty',
    'is empty'
  ),
  [FormattingRuleOperator.IS_NOT_EMPTY]: tr(
    'perspectives:operators.isNotEmpty',
    'is not empty'
  ),
  [FormattingRuleOperator.LIST_CONTAINS]: tr(
    'perspectives:operators.contains',
    'contains'
  ),
  [FormattingRuleOperator.LIST_DOES_NOT_CONTAIN]: tr(
    'perspectives:operators.doesNotContain',
    'does not contain'
  ),
};

const NOT_SET_LABEL = tr('perspectives:operators.notSet', 'not set');

const newFilterInfoOperatorLabels: Record<FormattingOperator, OperatorLabel> = {
  ...filterInfoOperatorLabels,
  [FilterInfoOperator.ID_MATCH]: tr('perspectives:operators.is', 'is'),
  [FormattingRuleOperator.ID_DOES_NOT_MATCH]: tr(
    'perspectives:operators.isNot',
    'is not'
  ),
  [FilterInfoOperator.EQUALS]: tr('perspectives:operators.is', 'is'),
  [FormattingRuleOperator.NOT_EQUAL]: tr(
    'perspectives:operators.isNot',
    'is not'
  ),
  [FilterInfoOperator.BOOLEAN]: tr('perspectives:operators.checked', 'checked'),
  [FilterInfoOperator.DATE_EARLIER]: tr(
    'perspectives:operators.isBefore',
    'is before'
  ),
  [FilterInfoOperator.DATE_LATER]: tr(
    'perspectives:operators.isAfter',
    'is after'
  ),
  [FormattingRuleOperator.DATE_ON_OR_BEFORE]: tr(
    'perspectives:operators.isOnOrBefore',
    'is on or before'
  ),
  [FormattingRuleOperator.DATE_ON_OR_AFTER]: tr(
    'perspectives:operators.isOnOrAfter',
    'is on or after'
  ),
  [FilterInfoOperator.DATE_RANGE_AFTER]: tr(
    'perspectives:operators.isAfter',
    'is after'
  ),
  [FilterInfoOperator.DATE_RANGE_BEFORE]: tr(
    'perspectives:operators.isBefore',
    'is before'
  ),
  [FilterInfoOperator.DATE_RANGE_CONTAINS]: tr(
    'perspectives:operators.includes',
    'includes'
  ),
  [FilterInfoOperator.DATE_RANGE_DOES_NOT_CONTAIN]: tr(
    'perspectives:operators.doesNotInclude',
    'does not include'
  ),
  [FormattingRuleOperator.IS_EMPTY]: {
    [QueryBuilderFilterType.BOOLEAN]: NOT_SET_LABEL,
    [QueryBuilderFilterType.DATE]: NOT_SET_LABEL,
    [QueryBuilderFilterType.DATE_RANGE]: NOT_SET_LABEL,
    [QueryBuilderFilterType.DATE_TIME]: NOT_SET_LABEL,
    [DEFAULT_OPERATOR_LABEL_KEY]: tr(
      'perspectives:operators.noValue',
      'no value'
    ),
  },
};

export const resolveLabel = (
  i18nLocale: I18nLocale,
  label: OperatorLabel,
  filterType: QueryBuilderFilterType | undefined
): string => {
  if (typeof label === 'string') return label;
  if (isTranslatableLabel(label)) return renderLeaf(i18nLocale, label);
  // narrowed to per-filterType map
  const perTypeMap = label as Partial<
    Record<QueryBuilderFilterType, LabelLeaf>
  > & {
    [DEFAULT_OPERATOR_LABEL_KEY]: LabelLeaf;
  };
  const leaf =
    filterType && perTypeMap[filterType]
      ? perTypeMap[filterType]!
      : perTypeMap[DEFAULT_OPERATOR_LABEL_KEY];
  return renderLeaf(i18nLocale, leaf);
};

export const resolveOperatorLabel = (
  i18nLocale: I18nLocale,
  operator: FormattingOperator,
  filterType: QueryBuilderFilterType | undefined,
  useNewLabels: boolean = true
): string => {
  const label = (
    useNewLabels ? newFilterInfoOperatorLabels : filterInfoOperatorLabels
  )[operator];
  if (label === undefined) return operator;
  return resolveLabel(i18nLocale, label, filterType);
};

export const SelectComparator = ({
  comparators,
  selectedComparator,
  onValueChange,
  clickId,
  isNewConditionalFormatting,
  menuPortalTarget,
  filterType,
}: SelectOperatorProps) => {
  const { i18nLocale } = useFormattingContext();
  const isGrouped = isTwoDimensional<FormattingOperator>(comparators);
  const isReadOnly =
    selectedComparator &&
    ((!isGrouped && comparators.length < 2) ||
      (isGrouped && sum(comparators.map(({ length }) => length)) < 2));

  const isComparatorUnsupported =
    selectedComparator &&
    isNewConditionalFormatting &&
    !isEmpty(comparators) &&
    !comparators.flat().includes(selectedComparator);

  const options = isGrouped
    ? comparators.map(comparatorsGroup => ({
        options: comparatorsGroup.map(
          comparatorToSelectOption(
            i18nLocale,
            filterType,
            isNewConditionalFormatting
          )
        ),
      }))
    : comparators.map(
        comparatorToSelectOption(
          i18nLocale,
          filterType,
          isNewConditionalFormatting
        )
      );
  if (isReadOnly || isComparatorUnsupported) {
    return (
      <TextInput
        isReadOnly
        value={resolveOperatorLabel(
          i18nLocale,
          selectedComparator,
          filterType,
          isNewConditionalFormatting
        )}
      />
    );
  }
  return (
    <Select
      aria-label={t(
        i18nLocale,
        'perspectives:selectComparator.operatorAriaLabel',
        'Operator'
      )}
      placeholder={t(
        i18nLocale,
        'perspectives:selectComparator.placeholder',
        'Select operator...'
      )}
      value={selectedComparator}
      onValueChange={newSelectedComparator => {
        onValueChange(newSelectedComparator ?? undefined);
      }}
      options={options}
      clickId={clickId}
      dataTestId="select-operator"
      menuPortalTarget={menuPortalTarget}
    />
  );
};
